import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { db, tref } from '../lib/firebase.js'
import { onValue, ref } from 'firebase/database'
import { addRegistration } from '../lib/data.js'
import { startEntryCheckout, verifyEntryCheckout, formatFee, createPaypalOrder, capturePaypalOrder, loadPaypalSdk, PAYPAL_CLIENT_ID } from '../lib/payments.js'
import { registrationGate } from '../lib/registrationGate.js'
import { sendComms } from '../lib/commsClient.js'
import { laneEnabled, enabledMethods, stripePaymentMethodTypes } from '../lib/paymentLanes.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'
import {
  calcCourseHandicap, calcPlayingHandicap,
  normalizeGhin, isValidGhin, DEFAULT_ALLOWANCE,
} from '../lib/handicap.js'
import { lookupGhinHandicap } from '../lib/ghinLookup.js'
import { buildHandicapEntry, resolveAllowSelfReported, isSelfReportedBlocked } from '../lib/handicapEntry.js'

const PBCC_GREEN = CLUB.primaryColor
const PBCC_GOLD = CLUB.accentColor

export default function RegisterApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')
  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the registration QR code from the pro shop.')
      return
    }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  if (error) return <Centered><Card>{error}</Card></Centered>
  if (!tournament) return <Centered>Loading…</Centered>

  const info = tournament.info || {}
  const regs = tournament.registrations || {}
  const { registeredCount, max, full, closed, waitlisted, notYetOpen, deadlinePassed, status, opens } = registrationGate(info, regs)

  // Stripe redirect return: ?reg=<id>&paid=1 → confirm paid server-side (the
  // session id is read from the DB by the server, never the URL).
  const returnedRegId = params.get('reg')
  const paidReturn = params.get('paid') === '1'
  if (paidReturn && returnedRegId) {
    return <PaidReturn tournamentId={tournamentId} regId={returnedRegId} info={info} reg={regs[returnedRegId]} />
  }

  return (
    <div className="min-h-full relative" style={{ backgroundColor: PBCC_GREEN }}>
      <PageNav back />
      <div className="max-w-md mx-auto p-6 pt-12">
        <header className="text-center mb-6">
          <img src={CLUB.logo} alt="" className="h-20 mx-auto mb-3 bg-white rounded-2xl p-1.5" />
          <p className="text-xs uppercase tracking-widest" style={{ color: PBCC_GOLD }}>{info.course || 'Tournament Registration'}</p>
          <h1 className="text-2xl font-extrabold text-white mt-1">{info.name}</h1>
          <p className="text-sm text-white/70">{info.date} · {info.format || '4-Ball Best Ball'}</p>
        </header>

        <div className="rounded-xl px-4 py-3 mb-4 flex justify-between items-center"
             style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40` }}>
          <span className="text-xs uppercase tracking-widest text-white/70">{waitlisted ? 'Waitlist' : 'Registered'}</span>
          <span className="font-extrabold" style={{ color: PBCC_GOLD }}>
            {registeredCount}{max ? ` of ${max}` : ''}
          </span>
        </div>

        {closed
          ? <ClosedCard status={status} full={full} deadlinePassed={deadlinePassed} notYetOpen={notYetOpen} opens={opens} />
          : <RegisterForm tournamentId={tournamentId} info={info} waitlisted={waitlisted} />
        }
      </div>
    </div>
  )
}

// Stripe return confirmation. Verifies paid status server-side, then reflects it
// from the live DB record. No payment data is read from the URL.
function PaidReturn({ tournamentId, regId, info, reg }) {
  const [state, setState] = useState('checking') // checking | paid | pending
  useEffect(() => {
    let alive = true
    // The payment RECEIPT is sent server-side from the webhook/capture (fires even
    // if the browser closes). Here we only confirm paid status for the UI.
    verifyEntryCheckout(tournamentId, regId)
      .then((r) => { if (alive) setState(r?.status === 'paid' ? 'paid' : 'pending') })
      .catch(() => { if (alive) setState('pending') })
    return () => { alive = false }
  }, [tournamentId, regId])
  const paid = state === 'paid' || reg?.payment?.status === 'paid'
  return (
    <div className="min-h-full relative" style={{ backgroundColor: PBCC_GREEN }}>
      <PageNav back />
      <div className="max-w-md mx-auto p-6 pt-12">
        <header className="text-center mb-6">
          <img src={CLUB.logo} alt="" className="h-20 mx-auto mb-3 bg-white rounded-2xl p-1.5" />
          <h1 className="text-2xl font-extrabold text-white mt-1">{info.name}</h1>
        </header>
        <Card>
          <div className="text-5xl text-center mb-2">{paid ? '✓' : '…'}</div>
          <p className="text-center font-extrabold text-white text-lg">
            {paid ? 'Entry fee paid' : state === 'checking' ? 'Confirming payment' : 'Payment pending'}
          </p>
          <p className="text-center text-sm text-white/75 mt-2">
            {paid ? "You're registered and paid." : 'This will update once the payment settles.'}
          </p>
        </Card>
      </div>
    </div>
  )
}

// PayPal / Venmo / card buttons (PayPal JS SDK). createOrder + onApprove both go
// through the Cloud Functions — the capture is verified server-side before paid
// status is written, so nothing here is trusted client-side.
function PaypalButtons({ tournamentId, regId, currency, onPaid }) {
  const holder = useRef(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let cancelled = false
    loadPaypalSdk(currency)
      .then((paypal) => {
        if (cancelled || !holder.current) return
        holder.current.innerHTML = ''
        paypal.Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' },
          createOrder: () => createPaypalOrder(tournamentId, regId),
          onApprove: async (data) => {
            const r = await capturePaypalOrder(tournamentId, regId, data.orderID)
            if (r?.status === 'paid') onPaid?.()
            else setErr('Payment not completed.')
          },
          onError: () => setErr('PayPal payment could not be completed.'),
        }).render(holder.current)
      })
      .catch((e) => setErr(e?.message || 'Could not load PayPal.'))
    return () => { cancelled = true }
  }, [tournamentId, regId, currency, onPaid])
  return (
    <div>
      <div ref={holder} />
      {err ? <p className="text-center text-sm mt-2" style={{ color: '#FCA5A5' }}>{err}</p> : null}
    </div>
  )
}

function ClosedCard({ status, full, deadlinePassed, notYetOpen, opens }) {
  let msg = 'Registration is closed.'
  if (notYetOpen) msg = opens ? `Registration opens ${opens.toLocaleDateString()}.` : 'Registration is not open yet.'
  else if (full) msg = 'Registration is full.'
  else if (deadlinePassed) msg = 'Registration deadline has passed.'
  else if (status === 'waitlist') msg = 'Registration is full — joining the waitlist below.'
  return (
    <Card>
      <p className="text-center font-bold text-white text-lg">{msg}</p>
      <p className="text-center text-sm text-white/70 mt-2">
        Contact the tournament committee if you believe this is an error.
      </p>
    </Card>
  )
}

function RegisterForm({ tournamentId, info, waitlisted = false }) {
  // Tournament-level handicap settings drive what extra fields render
  // per player. Defaults keep the form behaving exactly as before for
  // tournaments that never set these (mode === 'none').
  const handicapMode = info?.handicapMode || 'none'
  const slopeRating = Number(info?.slopeRating) || 113
  // Course rating + par feed the full USGA WHS formula. Older
  // tournaments that pre-date the course-search feature won't have
  // them set — calcCourseHandicap silently falls back to the slope-
  // only form in that case.
  const courseRating = Number(info?.courseRating) || null
  const coursePar = Number(info?.coursePar) || null
  const allowance = Number.isFinite(Number(info?.handicapAllowance))
    ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE
  const showHandicap = handicapMode !== 'none'
  // When false, only a (GH)#-verified index is accepted (manual field hidden,
  // submit blocked unless every player verified). Default: required → false.
  const allowSelfReported = resolveAllowSelfReported(info)

  // Players per team comes from the event's registration settings. Defaults
  // to 2 (the 4-Ball pair) so events created before this setting existed keep
  // behaving exactly as before. 1 = individual entry.
  const teamSize = Math.max(1, Math.min(4, Number(info?.teamSize) || 2))

  const [teamName, setTeamName] = useState('')
  // Parallel per-player arrays, one slot per team member. Kept as strings so
  // inputs stay controlled while empty.
  const [names, setNames] = useState(() => Array(teamSize).fill(''))
  const [ghins, setGhins] = useState(() => Array(teamSize).fill(''))
  const [indexes, setIndexes] = useState(() => Array(teamSize).fill(''))
  // Per-player handicap SOURCE: 'ghin' once a GHIN lookup fills the index,
  // 'self' when it's typed by hand. Defaults 'self' (a bare index is
  // self-reported until a lookup verifies it).
  const [sources, setSources] = useState(() => Array(teamSize).fill('self'))
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState('')
  const [regId, setRegId] = useState(null)
  const [paying, setPaying] = useState(false)
  const [paid, setPaid] = useState(false)

  // Per-event division/flight pick + configurable custom fields.
  const divisions = Array.isArray(info?.divisions) ? info.divisions.filter(Boolean) : []
  const [division, setDivision] = useState(divisions[0] || '')
  const customFields = Array.isArray(info?.registrationFields) ? info.registrationFields.filter((f) => f && f.label) : []
  const fieldKey = (f) => f.key || f.label
  const [customVals, setCustomVals] = useState(() => Object.fromEntries(customFields.map((f) => [fieldKey(f), ''])))
  const fee = info?.entryFee

  const setAt = (setter) => (i, v) => setter((arr) => arr.map((x, k) => (k === i ? v : x)))
  const setNameAt = setAt(setNames)
  const setGhinAt = setAt(setGhins)
  const setIndexAt = setAt(setIndexes)
  const setSourceAt = setAt(setSources)

  const submit = async (e) => {
    e.preventDefault()
    setErr('')
    if (names.some((n) => !n.trim())) {
      setErr(teamSize === 1 ? 'Player name is required.' : 'All player names are required.')
      return
    }
    if (showHandicap && !allowSelfReported) {
      // (GH)#-verified only: every player must have completed a successful GHIN
      // lookup (source 'ghin') with a resolved index. Self-reported is blocked.
      for (let i = 0; i < teamSize; i++) {
        if (indexes[i] === '' || isSelfReportedBlocked(sources[i], allowSelfReported)) {
          setErr(`Player ${i + 1}: this event requires a (GH)#-verified handicap — enter a valid (GH)# so it can be verified.`)
          return
        }
      }
    } else if (showHandicap) {
      // Self-reported allowed — either a GHIN OR a manual index per player.
      for (let i = 0; i < teamSize; i++) {
        if (!isValidGhin(ghins[i]) && indexes[i] === '') {
          setErr(`Player ${i + 1} needs a (GH)# or a Handicap Index.`)
          return
        }
      }
    }
    for (const f of customFields) {
      if (f.required && !String(customVals[fieldKey(f)] || '').trim()) {
        setErr(`${f.label} is required.`)
        return
      }
    }
    setSubmitting(true)
    try {
      const cleanNames = names.map((n) => n.trim())
      const reg = {
        teamName: teamName.trim() || cleanNames.map((n) => n.split(' ')[0]).join(' / '),
        players: cleanNames,
        email: email.trim() || null,
        phone: phone.trim() || null,
      }
      if (divisions.length) reg.division = division
      if (customFields.length) reg.fields = customVals
      if (waitlisted) reg.waitlisted = true
      if (showHandicap) {
        // Parallel array — indexes line up with reg.players. Each entry carries
        // its SOURCE flag ('ghin' | 'self') captured at entry.
        reg.playerHandicaps = cleanNames.map((_, i) => buildHandicapEntry({
          ghin: ghins[i], index: indexes[i], source: sources[i],
          slopeRating, courseRating, coursePar, allowance,
        }))
      }
      const id = await addRegistration(tournamentId, reg)
      setRegId(id)
      setDone(true)
      // Auto confirmation (club toggle). Fire-and-forget — server suppresses opt-outs.
      if (info?.comms?.auto?.confirmation && (reg.email || reg.phone)) {
        sendComms({
          tournamentId,
          channel: reg.email ? 'email' : 'sms',
          subject: `You're registered — ${info.name || 'Tournament'}`,
          body: `Thanks ${reg.teamName}! You're registered for ${info.name || 'the tournament'}.`,
          filter: { scope: 'individual', regIds: [id] },
        }).catch(() => {})
      }
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    const feeSet = !!fee && Number(fee.amount) > 0 && !waitlisted && !!regId
    const stripeOn = feeSet && laneEnabled(info, 'stripe') && stripePaymentMethodTypes(info).length > 0
    const paypalOn = feeSet && enabledMethods(info, 'paypal').length > 0 && !!PAYPAL_CLIENT_ID
    const payStripe = async () => {
      setErr(''); setPaying(true)
      try { await startEntryCheckout(tournamentId, regId) }
      catch (e) { setErr(e?.message || 'Could not start checkout.'); setPaying(false) }
    }
    return (
      <Card>
        <div className="text-5xl text-center mb-2">{paid ? '✓' : waitlisted ? '✓' : '✓'}</div>
        <p className="text-center font-extrabold text-white text-lg">
          {paid ? 'Entry fee paid' : waitlisted ? "You're on the waitlist" : "You're registered!"}
        </p>
        {err ? <p className="text-center text-sm mt-2" style={{ color: '#FCA5A5' }}>{err}</p> : null}
        {!paid && stripeOn ? (
          <button
            type="button"
            onClick={payStripe}
            disabled={paying}
            className="w-full mt-4 py-3 rounded-xl font-extrabold text-base uppercase tracking-wide transition active:scale-[0.99] disabled:opacity-50"
            style={{ backgroundColor: PBCC_GOLD, color: PBCC_GREEN }}>
            {paying ? 'Opening checkout…' : `Pay Entry Fee · ${formatFee(fee)}`}
          </button>
        ) : null}
        {!paid && paypalOn ? (
          <div className="mt-4">
            <PaypalButtons
              tournamentId={tournamentId}
              regId={regId}
              currency={(fee.currency || 'USD').toUpperCase()}
              onPaid={() => setPaid(true)}
            />
          </div>
        ) : null}
      </Card>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label={teamSize === 1 ? 'Display Name' : 'Team Name'}>
        <input className="input" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Auto-generated if blank" />
      </Field>
      {names.map((nm, i) => (
        <PlayerBlock
          key={i}
          idx={i + 1}
          name={nm} setName={(v) => setNameAt(i, v)}
          ghin={ghins[i]} setGhin={(v) => setGhinAt(i, v)}
          index={indexes[i]} setIndex={(v) => setIndexAt(i, v)}
          setSource={(v) => setSourceAt(i, v)}
          handicapMode={handicapMode}
          allowSelfReported={allowSelfReported}
          slopeRating={slopeRating}
          courseRating={courseRating}
          coursePar={coursePar}
          allowance={allowance}
          autoFocus={i === 0} />
      ))}
      <Field label="Email">
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoCapitalize="none" />
      </Field>
      <Field label="Phone">
        <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="555-0123" />
      </Field>
      {divisions.length > 0 ? (
        <Field label="Division">
          <select className="input" value={division} onChange={(e) => setDivision(e.target.value)}>
            {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
      ) : null}
      {customFields.map((f) => (
        <Field key={fieldKey(f)} label={`${f.label}${f.required ? ' *' : ''}`}>
          <input
            className="input"
            value={customVals[fieldKey(f)] || ''}
            onChange={(e) => setCustomVals((m) => ({ ...m, [fieldKey(f)]: e.target.value }))}
          />
        </Field>
      ))}
      {err ? <p className="text-red-300 text-sm">{err}</p> : null}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-4 rounded-2xl font-extrabold text-base uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-50"
        style={{
          background: `linear-gradient(180deg, ${PBCC_GOLD} 0%, var(--ge-primary) 100%)`,
          color: PBCC_GREEN,
          boxShadow: '0 6px 18px rgba(var(--ge-primary-rgb),0.4)',
        }}>
        {submitting ? 'Registering…' : 'Register Now'}
      </button>
    </form>
  )
}

function PlayerBlock({
  idx, name, setName, ghin, setGhin, index, setIndex, setSource,
  handicapMode, allowSelfReported = true, slopeRating, courseRating, coursePar, allowance, autoFocus,
}) {
  const ghinRequired = handicapMode === 'required'
  const ghinOptional = handicapMode === 'optional'
  const showHandicap = handicapMode !== 'none'

  // Live GHIN lookup status per player block. Values:
  //   'idle'     — nothing tried yet
  //   'loading'  — POST /golfer_login + GET /golfers in flight
  //   'ok'       — handicap-index pulled successfully; manual field hidden
  //   'notfound' — GHIN didn't resolve to a golfer
  //   'auth'     — couldn't authenticate against api.ghin.com
  //   'network'  — fetch threw / non-OK HTTP
  const [ghinStatus, setGhinStatus] = useState('idle')
  const runGhinLookup = async () => {
    const clean = (ghin || '').replace(/\D/g, '')
    if (clean.length < 7) {
      // Too short to be a real GHIN — clear any prior "ok" so the
      // manual Index field comes back into view.
      setGhinStatus(clean.length === 0 ? 'idle' : 'notfound')
      return
    }
    setGhinStatus('loading')
    const res = await lookupGhinHandicap(clean)
    if (res.ok) {
      setIndex(String(res.handicapIndex))
      setSource?.('ghin') // verified from the GHIN service
      // Auto-fill the player's name from the GHIN record. Only overwrite
      // when GHIN actually returned a non-empty fullName — keeps any
      // teammate name the player already typed if the API didn't carry
      // a name for this golfer (rare but possible).
      if (res.fullName) setName(res.fullName)
      setGhinStatus('ok')
    } else {
      setGhinStatus(res.reason || 'notfound')
    }
  }

  // Manual Handicap Index field stays visible until a GHIN lookup
  // actually succeeds. This replaces the old "for now the player still
  // types it in" placeholder behaviour referenced by the previous
  // comment at this position.
  // Manual index only when self-reporting is allowed; otherwise a GHIN lookup
  // is the only way to set it.
  const showIndex = showHandicap && ghinStatus !== 'ok' && allowSelfReported
  const idxNum = index === '' ? null : Number(index)
  const ch = idxNum == null ? null : calcCourseHandicap(idxNum, slopeRating, courseRating, coursePar)
  const ph = ch == null ? null : calcPlayingHandicap(ch, allowance)
  return (
    <div className="space-y-3">
      <Field label={`Player ${idx} *`}>
        <input className="input" autoFocus={autoFocus} value={name}
          onChange={(e) => setName(e.target.value)} placeholder="First Last" />
      </Field>
      {showHandicap ? (
        <>
          <Field label={`(GH)#${ghinRequired ? ' *' : ''}`}>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              value={ghin}
              onChange={(e) => {
                setGhin(e.target.value)
                // Typing invalidates whatever we last looked up.
                if (ghinStatus !== 'idle') setGhinStatus('idle')
              }}
              onBlur={runGhinLookup}
              placeholder="e.g. 1234567"
              maxLength={10} />
          </Field>
          <GhinStatusLine status={ghinStatus} index={index} />
          {showIndex ? (
            <Field label={`Handicap Index${ghinOptional ? ' (if no (GH)#)' : ' *'}`}>
              <input
                className="input"
                type="number"
                step="0.1"
                value={index}
                onChange={(e) => { setIndex(e.target.value); setSource?.('self') }}
                placeholder="e.g. 12.4" />
            </Field>
          ) : null}
          {ch != null ? (
            <div className="rounded-xl px-4 py-3 flex justify-between text-sm"
                 style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40`, color: '#fff' }}>
              <span className="uppercase tracking-widest text-white/70 text-xs">
                Course HCP / Playing HCP
              </span>
              <span className="font-extrabold" style={{ color: PBCC_GOLD }}>{ch} / {ph}</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function Card({ children }) {
  return (
    <div className="rounded-2xl p-5"
         style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40` }}>
      {children}
    </div>
  )
}

// Per-row status under the GHIN field. Dark-theme styling matched to
// the rest of the public registration form. `index` is shown alongside
// the success message so the player can see what we filled in.
function GhinStatusLine({ status, index }) {
  if (status === 'loading') {
    return (
      <p className="text-xs text-white/80" role="status">
        Looking up handicap…
      </p>
    )
  }
  if (status === 'ok') {
    return (
      <p className="text-xs font-bold" style={{ color: PBCC_GOLD }} role="status">
        ✓ Handicap Index pulled from handicap lookup: <span className="font-extrabold">{index}</span>
      </p>
    )
  }
  if (status === 'notfound') {
    return (
      <p className="text-xs text-red-300" role="status">
        Handicap number not found — enter your Handicap Index manually below.
      </p>
    )
  }
  if (status === 'auth' || status === 'network') {
    return (
      <p className="text-xs text-red-300" role="status">
        Couldn't reach the handicap lookup service — enter your Handicap Index manually below.
      </p>
    )
  }
  return null
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold mb-1 block" style={{ color: PBCC_GOLD }}>{label}</span>
      {children}
      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.95);
          color: #111;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 16px;
          border: 1px solid rgba(255,255,255,0.3);
        }
        .input:focus { outline: 2px solid ${PBCC_GOLD}; outline-offset: 1px; }
      `}</style>
    </label>
  )
}

function Centered({ children }) {
  return (
    <div className="min-h-full flex items-center justify-center p-6" style={{ backgroundColor: PBCC_GREEN, color: PBCC_GOLD }}>
      {children}
    </div>
  )
}
